import { useContext, useEffect, useState } from "react";
import { save, trash } from "../../assets/icons";
import Card from "../../components/card/Card";
import Inline from "../../components/inline/Inline";
import { TextInput } from "../../components/inputs/TextInput";
import { Page, PageBody, PageHeader } from "../../components/layout/Page";
import "./settings.css";
import { HoverButton } from "../../components/buttons/HoverButton";
import { useListRecords } from "../../server/hooks/getData";
import {
  handleNewStatus,
  handleStatusChange,
  handleStatusDelete,
  handleStatusUpdate,
} from "./statusFunctions";
import { createRecord, deleteRecord, updateRecord } from "../../server/fetchFunctions";
import Form from "../../components/inputs/Form";
import { pattern } from "../../lib/pattern";
import { UserContext } from "../../components/layout/Root";

type SetValue = React.Dispatch<React.SetStateAction<any>>;

export default function Settings() {
  const { user, setUser } = useContext(UserContext);
  const [color, setColor] = useState(user.theme_color_hs || 250);

  const [newStatus, setNewStatus] = useState<{ [key: string]: any }>({
    status_name: "",
    color: "",
  });
  const [newEtalonFunc, setNewEtalonFunc] = useState<{ [key: string]: any }>({
    function: "",
    sort_order: "",
  });
  const [newDeviceFunc, setNewDeviceFunc] = useState<{ [key: string]: any }>({
    function: "",
    sort_order: "",
  });
  const [newEmloyee, setNewEmloyee] = useState<{ [key: string]: any }>({
    name: "",
  });
  const { data: statuses, loading: statusL } = useListRecords("status", [], {});
  const { data: counterList, loading: counterL } = useListRecords("counters", [], {});
  const { data: serviceFeeList, loading: serviceFeeL } = useListRecords("serviceFee", [], {});
  const { data: etalonFunctionList, loading: etalonFunctionL } = useListRecords("etalonFunc", [], {
    orderBy: "sort_order",
    order: "ASC",
  });
  const { data: deviceFunctionList, loading: deviceFunctionL } = useListRecords("deviceFunc", [], {
    orderBy: "sort_order",
    order: "ASC",
  });
  const { data: employeeList, loading: employeeL } = useListRecords("employee", [], {
    orderBy: "name",
    order: "ASC",
  });
  const [status, setStatus] = useState(statuses.data || []);
  const [counters, setCounters] = useState(counterList.data || []);
  const [serviceFee, setServiceFee] = useState(serviceFeeList.data || []);
  const [etalonFunction, setEtalonFunction] = useState(etalonFunctionList.data || []);
  const [deviceFunction, setDeviceFunction] = useState(deviceFunctionList.data || []);
  const [employees, setEmployees] = useState(employeeList.data || []);

  useEffect(() => {
    if (statuses.data && counterList.data && serviceFeeList.data) {
      setStatus(statuses.data);
      setCounters(counterList.data);
      setServiceFee(serviceFeeList.data);
      setEtalonFunction(etalonFunctionList.data);
      setDeviceFunction(deviceFunctionList.data);
      setEmployees(employeeList.data);
    }
  }, [
    statuses.data,
    counterList.data,
    serviceFeeList.data,
    etalonFunctionList.data,
    deviceFunctionList.data,
    employeeList.data,
  ]);

  const onColor = (h: any) => {
    setColor(h);
    setUser((prev: any) => ({ ...prev, theme_color_hs: h }));
    const root = document.documentElement;
    root.style.setProperty("--h", color?.toString());
  };

  const handleUpdate = async (e: any, value: any, route: string) => {
    e.preventDefault();
    const id = value.id;
    const data = value;
    await updateRecord(route, id, data);
  };

  const handleCreate = async (
    e: any,
    value: any,
    setValue: SetValue,
    setNewValue: SetValue,
    route: string
  ) => {
    e.preventDefault();
    const response = await createRecord(route, value);
    setValue((prev: any[]) => [...prev, response]);
    setNewValue({});
  };

  const handleDelete = async (id: number, setValue: SetValue, route: string) => {
    await deleteRecord(route, id);
    setValue((prev: any[]) => prev.filter(item => item.id !== id));
  };

  return (
    <Page>
      <PageHeader title="Beállítások"></PageHeader>
      <PageBody>
        <div className="settings">
          <Card title="Státuszok">
            <Inline>
              <div style={{ flexGrow: 1, display: "flex" }}>
                <TextInput
                  stateKey="status_name"
                  inlineLabel="Új Név"
                  state={newStatus}
                  setState={setNewStatus}
                  pattern={pattern.normal_text}
                  required
                />
              </div>
              <div style={{ flexGrow: 1, display: "flex" }}>
                <TextInput
                  stateKey="color"
                  inlineLabel="Szín"
                  state={newStatus}
                  setState={setNewStatus}
                  pattern={pattern.normal_text}
                  required
                />
              </div>
              <div style={{ flexGrow: 0, display: "flex" }}>
                <HoverButton
                  onClick={() => handleNewStatus(newStatus, setStatus, statuses, setNewStatus)}
                >
                  {save}
                </HoverButton>
              </div>
            </Inline>
            <ul style={{ marginTop: 10 }}>
              {!statusL &&
                status.map((item, index) => (
                  <li key={index}>
                    <Inline style={{ marginBottom: 10 }}>
                      <div style={{ flexGrow: 1, display: "flex" }}>
                        <TextInput
                          stateKey="status_name"
                          inlineLabel="Név"
                          state={item}
                          setState={setStatus}
                          itemOfArr={item}
                          pattern={pattern.normal_text}
                          required
                        />
                      </div>
                      <div style={{ flexGrow: 1, display: "flex" }}>
                        <TextInput
                          stateKey="color"
                          inlineLabel="Szín"
                          state={item}
                          setState={setStatus}
                          itemOfArr={item}
                          pattern={pattern.normal_text}
                          required
                        />
                      </div>
                      <div style={{ flexGrow: 0, display: "flex" }}>
                        <HoverButton
                          onClick={() =>
                            JSON.stringify(statuses.data[index]) === JSON.stringify(status[index])
                              ? handleStatusDelete(item.id, setStatus)
                              : handleStatusUpdate(item.id, status[index])
                          }
                        >
                          {JSON.stringify(statuses.data[index]) === JSON.stringify(status[index])
                            ? trash
                            : save}
                        </HoverButton>
                      </div>
                    </Inline>
                  </li>
                ))}
            </ul>
          </Card>
          <Card title="Számlálók">
            {!counterL && (
              <>
                <Form
                  onSubmit={e =>
                    handleUpdate(
                      e,
                      counters.find((item: any) => item.name === "jobnumber"),
                      "counters"
                    )
                  }
                >
                  <p style={{ marginBottom: 10, marginTop: 10 }}>Munkaszám</p>
                  <Inline style={{ marginBottom: 10 }}>
                    <div style={{ flexGrow: 1, display: "flex" }}>
                      {counters?.map(
                        (item: any, index: any) =>
                          item.name === "jobnumber" && (
                            <TextInput
                              key={item.id}
                              stateKey="prefix"
                              inlineLabel="Előtag"
                              state={item}
                              setState={setCounters}
                              itemOfArr={item}
                              pattern={pattern.normal_text}
                              required
                            />
                          )
                      )}
                    </div>
                    <div style={{ flexGrow: 0, display: "flex" }}>
                      <HoverButton type="submit">{save}</HoverButton>
                    </div>
                  </Inline>
                </Form>
                <Form
                  onSubmit={e =>
                    handleUpdate(
                      e,
                      counters.find((item: any) => item.name === "jobnumber"),
                      "counters"
                    )
                  }
                >
                  <Inline style={{ marginBottom: 10 }}>
                    <div style={{ flexGrow: 1, display: "flex" }}>
                      {counters?.map(
                        (item: any, index: any) =>
                          item.name === "jobnumber" && (
                            <TextInput
                              key={item.id}
                              stateKey="next_number"
                              inlineLabel="Következő szám"
                              state={item}
                              setState={setCounters}
                              itemOfArr={item}
                              format="toNumber"
                              required
                            />
                          )
                      )}
                    </div>
                    <div style={{ flexGrow: 0, display: "flex" }}>
                      <HoverButton type="submit">{save}</HoverButton>
                    </div>
                  </Inline>
                </Form>
              </>
            )}
            <div style={{ marginBottom: 10 }}></div>
            {!counterL && (
              <>
                <Form
                  onSubmit={e =>
                    handleUpdate(
                      e,
                      counters.find((item: any) => item.name === "calnumber"),
                      "counters"
                    )
                  }
                >
                  <p style={{ marginBottom: 10, marginTop: 10 }}>Kalibrálási szám</p>
                  <Inline style={{ marginBottom: 10 }}>
                    <div style={{ flexGrow: 1, display: "flex" }}>
                      {counters?.map(
                        (item: any, index: any) =>
                          item.name === "calnumber" && (
                            <TextInput
                              key={item.id}
                              stateKey="prefix"
                              inlineLabel="Előtag szám"
                              state={item}
                              setState={setCounters}
                              itemOfArr={item}
                              pattern={pattern.normal_text}
                              required
                            />
                          )
                      )}
                    </div>
                    <div style={{ flexGrow: 0, display: "flex" }}>
                      <HoverButton type="submit">{save}</HoverButton>
                    </div>
                  </Inline>
                </Form>
                <Form
                  onSubmit={e =>
                    handleUpdate(
                      e,
                      counters.find((item: any) => item.name === "calnumber"),
                      "counters"
                    )
                  }
                >
                  <Inline style={{ marginBottom: 10 }}>
                    <div style={{ flexGrow: 1, display: "flex" }}>
                      {counters?.map(
                        (item: any, index: any) =>
                          item.name === "calnumber" && (
                            <TextInput
                              key={item.id}
                              stateKey="next_number"
                              inlineLabel="Következő szám"
                              state={item}
                              setState={setCounters}
                              itemOfArr={item}
                              format="toNumber"
                              required
                            />
                          )
                      )}
                    </div>
                    <div style={{ flexGrow: 0, display: "flex" }}>
                      <HoverButton type="submit">{save}</HoverButton>
                    </div>
                  </Inline>
                </Form>
              </>
            )}
          </Card>

          <Card title="Szolgáltatási költségek">
            {!counterL && (
              <>
                <Form
                  onSubmit={e =>
                    handleUpdate(
                      e,
                      serviceFee.find((item: any) => item.service === "qualify"),
                      "serviceFee"
                    )
                  }
                >
                  <Inline style={{ marginBottom: 10 }}>
                    <div style={{ flexGrow: 1, display: "flex" }}>
                      {serviceFee?.map(
                        (item: any, index: any) =>
                          item.service === "qualify" && (
                            <TextInput
                              key={item.id}
                              stateKey="amount"
                              inlineLabel="Minősítés"
                              state={item}
                              setState={setServiceFee}
                              itemOfArr={item}
                              format="toNumber"
                              required
                            />
                          )
                      )}
                    </div>
                    <div style={{ flexGrow: 0, display: "flex" }}>
                      <HoverButton type="submit">{save}</HoverButton>
                    </div>
                  </Inline>
                </Form>
                <Form
                  onSubmit={e =>
                    handleUpdate(
                      e,
                      serviceFee.find((item: any) => item.service === "examination"),
                      "serviceFee"
                    )
                  }
                >
                  <Inline style={{ marginBottom: 10 }}>
                    <div style={{ flexGrow: 1, display: "flex" }}>
                      {serviceFee?.map(
                        (item: any, index: any) =>
                          item.service === "examination" && (
                            <TextInput
                              key={item.id}
                              stateKey="amount"
                              inlineLabel="Bevizsgálás"
                              state={item}
                              setState={setServiceFee}
                              itemOfArr={item}
                              format="toNumber"
                              required
                            />
                          )
                      )}
                    </div>
                    <div style={{ flexGrow: 0, display: "flex" }}>
                      <HoverButton type="submit">{save}</HoverButton>
                    </div>
                  </Inline>
                </Form>
              </>
            )}
          </Card>

          <Card title="Munkatársak">
            <Inline>
              <div style={{ flexGrow: 1, display: "flex" }}>
                <TextInput
                  stateKey="name"
                  inlineLabel="Új Munkatárs"
                  state={newEmloyee}
                  setState={setNewEmloyee}
                  pattern={pattern.normal_text}
                  required
                />
              </div>
              <div style={{ flexGrow: 0, display: "flex" }}>
                <HoverButton
                  onClick={e =>
                    handleCreate(e, newEmloyee, setEmployees, setNewEmloyee, "employee")
                  }
                >
                  {save}
                </HoverButton>
              </div>
            </Inline>
            <ul style={{ marginTop: 10 }}>
              {!employeeL &&
                employees.map((item, index) => (
                  <li key={index}>
                    <Inline style={{ marginBottom: 10 }}>
                      <div style={{ flexGrow: 1, display: "flex" }}>
                        <TextInput
                          stateKey="name"
                          inlineLabel="Munkatárs"
                          state={item}
                          setState={setEmployees}
                          itemOfArr={item}
                          pattern={pattern.normal_text}
                          required
                        />
                      </div>
                      <div style={{ flexGrow: 0, display: "flex" }}>
                        <HoverButton
                          onClick={e =>
                            JSON.stringify(employeeList.data[index]) ===
                            JSON.stringify(employees[index])
                              ? handleDelete(item.id, setEmployees, "employee")
                              : handleUpdate(e, item, "employee")
                          }
                        >
                          {JSON.stringify(employeeList.data[index]) ===
                          JSON.stringify(employees[index])
                            ? trash
                            : save}
                        </HoverButton>
                      </div>
                    </Inline>
                  </li>
                ))}
            </ul>
          </Card>
          <Card title="Téma">
            <p>Színárnyalat</p>
            <Inline>
              <div style={{ flexGrow: 1, display: "flex" }}>
                <input
                  style={{ width: "100%" }}
                  type="range"
                  min={0}
                  max={360}
                  value={color}
                  onChange={(e: any) => onColor(e.target.value)}
                />
              </div>
              <div style={{ flexGrow: 0, display: "flex" }}>
                <HoverButton
                  onClick={async () =>
                    await updateRecord("user", user.id, { theme_color_hs: color })
                  }
                >
                  {save}
                </HoverButton>
              </div>
              <div style={{ flexGrow: 0, display: "flex" }}>
                <HoverButton
                  onClick={async () => {
                    await updateRecord("user", user.id, { theme_color_hs: null });
                    onColor(250);
                  }}
                >
                  {"Eredeti"}
                </HoverButton>
              </div>
            </Inline>
            <p>Szaturáció</p>
            <Inline>
              <div style={{ flexGrow: 1, display: "flex" }}>
                <input
                  style={{ width: "100%" }}
                  type="range"
                  min={0}
                  max={360}
                  value={color}
                  onChange={(e: any) => onColor(e.target.value)}
                />
              </div>
              <div style={{ flexGrow: 0, display: "flex" }}>
                <HoverButton
                  onClick={async () =>
                    await updateRecord("user", user.id, { theme_color_hs: color })
                  }
                >
                  {save}
                </HoverButton>
              </div>
              <div style={{ flexGrow: 0, display: "flex" }}>
                <HoverButton
                  onClick={async () => {
                    await updateRecord("user", user.id, { theme_color_hs: null });
                    onColor(250);
                  }}
                >
                  {"Eredeti"}
                </HoverButton>
              </div>
            </Inline>
          </Card>
        </div>
        <Card title="Etalon funkciók" style={{ marginTop: 10 }}>
          <Inline>
            <div style={{ flexGrow: 1, display: "flex" }}>
              <TextInput
                stateKey="function"
                inlineLabel="Új Funkció"
                state={newEtalonFunc}
                setState={setNewEtalonFunc}
                pattern={pattern.normal_text}
                required
              />
            </div>
            <div style={{ flexGrow: 1, display: "flex" }}>
              <TextInput
                stateKey="sort_order"
                inlineLabel="Sorrend"
                state={newEtalonFunc}
                setState={setNewEtalonFunc}
                format="toNumber"
                required
              />
            </div>
            <div style={{ flexGrow: 0, display: "flex" }}>
              <HoverButton
                onClick={e =>
                  handleCreate(e, newEtalonFunc, setEtalonFunction, setNewEtalonFunc, "etalonFunc")
                }
              >
                {save}
              </HoverButton>
            </div>
          </Inline>
          <ul style={{ marginTop: 10 }}>
            {!etalonFunctionL &&
              etalonFunction.map((item, index) => (
                <li key={index}>
                  <Inline style={{ marginBottom: 10 }}>
                    <div style={{ flexGrow: 1, display: "flex" }}>
                      <TextInput
                        stateKey="function"
                        inlineLabel="Funkció"
                        state={item}
                        setState={setEtalonFunction}
                        itemOfArr={item}
                        pattern={pattern.normal_text}
                        required
                      />
                    </div>
                    <div style={{ flexGrow: 1, display: "flex" }}>
                      <TextInput
                        stateKey="sort_order"
                        inlineLabel="Sorrend"
                        state={item}
                        setState={setEtalonFunction}
                        itemOfArr={item}
                        format="toNumber"
                        required
                      />
                    </div>
                    <div style={{ flexGrow: 0, display: "flex" }}>
                      <HoverButton
                        onClick={e =>
                          JSON.stringify(etalonFunctionList.data[index]) ===
                          JSON.stringify(etalonFunction[index])
                            ? handleDelete(item.id, setEtalonFunction, "etalonFunc")
                            : handleUpdate(e, item, "etalonFunc")
                        }
                      >
                        {JSON.stringify(etalonFunctionList.data[index]) ===
                        JSON.stringify(etalonFunction[index])
                          ? trash
                          : save}
                      </HoverButton>
                    </div>
                  </Inline>
                </li>
              ))}
          </ul>
        </Card>
        <Card title="Eszköz funkciók" style={{ marginTop: 10 }}>
          <Inline>
            <div style={{ flexGrow: 1, display: "flex" }}>
              <TextInput
                stateKey="function"
                inlineLabel="Új Funkció"
                state={newDeviceFunc}
                setState={setNewDeviceFunc}
                pattern={pattern.normal_text}
                required
              />
            </div>
            <div style={{ flexGrow: 1, display: "flex" }}>
              <TextInput
                stateKey="sort_order"
                inlineLabel="Sorrend"
                state={newDeviceFunc}
                setState={setNewDeviceFunc}
                format="toNumber"
                required
              />
            </div>
            <div style={{ flexGrow: 0, display: "flex" }}>
              <HoverButton
                onClick={e =>
                  handleCreate(e, newDeviceFunc, setDeviceFunction, setNewDeviceFunc, "deviceFunc")
                }
              >
                {save}
              </HoverButton>
            </div>
          </Inline>
          <ul style={{ marginTop: 10 }}>
            {!deviceFunctionL &&
              deviceFunction.map((item, index) => (
                <li key={index}>
                  <Inline style={{ marginBottom: 10 }}>
                    <div style={{ flexGrow: 1, display: "flex" }}>
                      <TextInput
                        stateKey="function"
                        inlineLabel="Funkció"
                        state={deviceFunction[index]}
                        setState={setDeviceFunction}
                        itemOfArr={item}
                        pattern={pattern.normal_text}
                        required
                      />
                    </div>
                    <div style={{ flexGrow: 1, display: "flex" }}>
                      <TextInput
                        stateKey="sort_order"
                        inlineLabel="Sorrend"
                        state={deviceFunction[index]}
                        setState={setDeviceFunction}
                        itemOfArr={item}
                        format="toNumber"
                        required
                      />
                    </div>
                    <div style={{ flexGrow: 0, display: "flex" }}>
                      <HoverButton
                        onClick={e =>
                          JSON.stringify(deviceFunctionList.data[index]) ===
                          JSON.stringify(deviceFunction[index])
                            ? handleDelete(item.id, setDeviceFunction, "deviceFunc")
                            : handleUpdate(e, item, "deviceFunc")
                        }
                      >
                        {JSON.stringify(deviceFunctionList.data[index]) ===
                        JSON.stringify(deviceFunction[index])
                          ? trash
                          : save}
                      </HoverButton>
                    </div>
                  </Inline>
                </li>
              ))}
          </ul>
        </Card>
      </PageBody>
    </Page>
  );
}
